import { HouseIcon } from "@phosphor-icons/react/dist/csr/House";
import * as Sentry from "@sentry/react";
import { useEffect, type PropsWithChildren } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { useEnv } from "@/core/env";

const ErrorFallback = ({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) => {
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      // Clear caches using the Cache API
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));

      // Clear service worker if present
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      resetError();
      // Force reload the page
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      // Fallback to simple reload if cache clearing fails
      window.location.href = "/";
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
      <h1 className="mb-2 text-4xl leading-[0.95] font-medium tracking-[-0.06em] text-ink">
        Something went wrong
      </h1>
      <p className="mb-4 text-sm leading-[1.5] text-subtle">
        Looks like there was an error with our system. Go back home and try
        again.
      </p>
      {import.meta.env.DEV && !!error && (
        <pre className="mb-8 max-w-2xl overflow-auto rounded-lg bg-neutral p-4 text-left text-xs">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      )}
      <Button
        variant="ghost"
        render={<a href="/" onClick={handleClick} />}
        nativeButton={false}
      >
        <HouseIcon data-icon="inline-start" />
        Back home
      </Button>
    </div>
  );
};

export const SentryProvider = ({ children }: PropsWithChildren) => {
  const { sentryDsn, appEnv } = useEnv();

  useEffect(() => {
    Sentry.init({
      dsn: sentryDsn,
      // The release the build's source maps were uploaded under.
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Sentry.replayIntegration(),
      ],
      // Performance Monitoring. API requests inherit this sampling decision
      // through the trace headers, so keep it in step with the API's rate.
      tracesSampleRate: 0.05,
      tracePropagationTargets: ["localhost", /^https?:\/\/.*\.govolca\.com/],
      // Session Replay
      // replaysSessionSampleRate: 0.1,
      // replaysOnErrorSampleRate: 1.0,
      // Set environment
      environment: appEnv || "development",
    });
  }, [sentryDsn, appEnv]);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryProvider;
